import { ColumnSource, RowSource, CellSource, CellModel } from "@omegagrid/grid";
import { ToolbarItem } from '@omegagrid/toolbar';
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { IndicatorDefinition } from '@reporting/rp-types/definition';
import { IndicatorGroup, AGGREGATIONS } from "../../types";
import { EntityAccessType } from "@reporting/rp-types/users";

export class ItemIndicatorGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];

	constructor(
		private group: IndicatorGroup,
	) {	super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'access',
				title: msg('Access'),
				icon: 'shield-check',
				color: ColorTypes.Gray
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'indicator',
			header: msg('Indicator'),
			filter: false,
			t: 't',
			e: false,
			w: 100
		}, {
			data: 'column',
			header: msg('Column'),
			filter: false,
			t: 't',
			e: false,
			w: 155
		}, {
			data: 'visible',
			header: msg('Visible'),
			t: 'b',
			e: true,
			w: 70
		}, {
			data: 'ord_num',
			header: msg('Order'),
			filter: false,
			t: 't',
			e: true,
			w: 60
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			e: true,
			w: 180
		}, {
			data: 'aggregation',
			header: msg('Aggregation'),
			t: 's',
			e: true,
			w: 120,
			content: AGGREGATIONS
		}, {
			data: 'dec_num',
			header: msg('Decimal digit count'),
			filter: false,
			t: 'n',
			e: true,
			w: 70
		}, {
			data: 'um',
			header: msg('Units'),
			t: 'b',
			e: true,
			w: 75
		}, {
			data: 'remark',
			header: msg('Remark'),
			filter: false,
			t: 't',
			e: true,
			w: 180
		}, {
			data: 'data_source',
			header: msg('Data source'),
			filter: false,
			t: 't',
			e: true,
			w: 180
		}];
		if (this.group === 'B') {
			this._columns.push({
				data: 'balance_type',
				header: msg('Balance type'),
				t: 's',
				e: true,
				w: 140,
				content: [
					{k: 1, v: 1},
					{k: 2, v: 2},
					{k: 3, v: 3},
					{k: 4, v: 4},
				]
			});
		}
		this._columns.push({
			data: 'id_indicator',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: 'type',
			t: 't',
			e: false,
			hidden: true
		}, {
			data: 'balance',
			t: 'b',
			e: false,
			hidden: true
		});
		return this._columns;
	}

	private createCell(ind: IndicatorDefinition, col: ColumnSource): Partial<CellSource> | null {
		if (col.hidden) return null;
		switch (col.data as string) {
			case 'indicator':
				const indicatorValue = `${ind.type == 'AMOUNT' ? msg('Amount') : msg('Quantity')} ${ind.id_indicator}`;
				return { v: indicatorValue };
			case 'column':
				const columnValue = `${this.group == 'B' ? 'BALANCE_' : ''}${ind.type}_${ind.id_indicator}`;
				return { v: columnValue };
			case 'um':
				if (ind.type !== 'QTY') return { e: false, t: 't', v: 'null', dv: '' };
		}
		return null;
	}

	async getRows(): Promise<RowSource[]> {
		const indicators = await rp.ms('definition').get<IndicatorDefinition[]>(`itemIndicators/${this.group}`);
		this._rows = [];
		for (const ind of indicators) {
			const rs: RowSource = { data: ind };
			for (const col of this._columns) {
				const cell = this.createCell(ind, col);
				if (cell) rs.data[col.data] = cell;
			}
			this._rows.push(rs);
		}
		return this._rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			pageSize: null,
			toolbar: {
				new: {enabled: false},
			},
			customToolbar: this.getCustomToolbar(),
		});
	}

	getCustomToolbar(): ToolbarItem[] {
		return [{
			id: 'addKpi',
			type: 'button',
			text: `${msg('Add')} KPI`,
			color: ColorTypes.Blue,
			icon: {icon: 'plus'}
		}];
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		await rp.ms('definition').post('itemIndicators', data);
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'access':
				const data = cell.row.data as IndicatorDefinition;
				const entity =
					data.type === 'QTY' ? this.group === 'B' ? EntityAccessType.ItemQtyBalance : EntityAccessType.ItemQty :
					data.type === 'AMOUNT' ? this.group === 'B' ? EntityAccessType.ItemAmountBalance : EntityAccessType.ItemAmount :
					null;
				if (!entity) return;
				rp.host.open(`users-entityUsers-${entity}-${data.id_indicator}`, { title: `${msg('Access')}: ${data.description}` });
				break;
			case 'addKpi':
				const allIndicatorsUsed = this._rows.every(row => row.data.idz);
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `
						${!allIndicatorsUsed ? msg('Not all available indicators have been used yet! ') : ''}
						${msg('Are you sure you want to create a new indicator?')}
					`
				});
				if (command?.id == 'Y') {
					this.editor.loader.show(this.editor);
					await rp.ms('definition').post('itemIndicators/add', { table: this.group });
					this.editor.refresh();
				}
				break;
		}
	}
}
